import { useEffect, useState } from "react"
import { AxiosResponse } from "axios"
import { httpGet } from "../services/Http.service"
import { apiUrl } from "../configs/config.constants"
import { Project } from "../types/project.type"
import { RentalAssetSummary } from "../types/rental.type"

/**
 * Whether the project rents anything out, so the rental entry points can stay hidden for
 * the projects that only take bookings.
 *
 * Deliberately counts every item rather than the available ones: a shop whose whole stock
 * is out today still rents, and hiding the page would tell the customer it does not exist
 * instead of showing them what to come back for.
 */
export function useHasRentalItems(project?: Project) {
  const [hasItems, setHasItems] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // The request is project-scoped through the `project-id` header, which is only stored
    // once the project has loaded.
    if (!project) return

    let active = true
    setIsLoading(true)

    httpGet(apiUrl.objectRental.assets)
      .then((response: AxiosResponse<{ data?: RentalAssetSummary[] }>) => {
        if (!active) return
        setHasItems((response.data?.data || []).length > 0)
      })
      .catch(() => {
        // An entry point that leads nowhere is worse than a missing one, so a failed
        // lookup is treated as "nothing to rent".
        if (active) setHasItems(false)
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })

    return () => {
      active = false
    }
  }, [project])

  return { hasItems, isLoading }
}
